import _ from 'lodash';
import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { format } from 'date-fns';

import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Select } from '../ui/newSelect';
import { Separator } from '../ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Label } from '../ui/label';
import { Actions as USAGE_ACTIONS } from '../../store/actions/usageActions';
import { Actions as ADMIN_ACTIONS } from '../../store/actions/adminActions';
import QualityTierBadge from '../Quality/QualityTierBadge';
import { BetaTag } from '../Common/ReusableComponents/Reusable';
import { parseDateToTz } from '../utils/GlobalFunctions';
import { getUsageQuality, getGroupUsageQuality } from '../../utils/qualityHelpers';
import { ChevronDown, ChevronRight, Pencil, Copy, Trash2, Loader2, Download, Info, FileText } from 'lucide-react';

const DATE_FORMAT = 'dd/MM/yyyy';

// The API returns the reporting year as a number (numeric fetch), a numeric string (explicit fetch),
// or 'all'. Normalise it so it always matches the filter option values.
const normalizeYear = (year) => {
  if (year == null || year === '') return null;
  if (year === 'all') return 'all';
  const numeric = Number(year);
  return Number.isNaN(numeric) ? year : numeric;
};

// Reporting-year window shown on grouped rows, matching the previous table:
// start = (endDate - 1 year + 1 day), end = endDate.
const getReportingWindow = (endDate) => {
  const end = moment(endDate);
  const start = moment(endDate).add(-1, 'year').add(1, 'day');
  return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
};

// Actual data dates shown on detail rows, matching the previous table.
const getDetailDateRange = (dateStart, dateEnd) => {
  const start = parseDateToTz(dateStart, 'yyyy-MM-dd');
  const end = dateEnd ? parseDateToTz(dateEnd, 'yyyy-MM-dd') : null;
  const startLabel = format(start, DATE_FORMAT);
  if (end && start && String(start) !== String(end)) {
    return `${startLabel} - ${format(end, DATE_FORMAT)}`;
  }
  return startLabel;
};

const UsageSummaryTable = ({
  usageTotals,
  usageData,
  facilities,
  entities,
  currencies,
  metrics,
  types,
  usageDetailLabels,
  currentOrganisation,
  orgDetails,
  getUsageTotals,
  usageDataYear,
  processing,
  showUsageDataModal,
  showUsageDataDeleteModal,
  getUsageOptions,
  canEdit,
  canDelete,
  canCopy,
  getItemsByTermsFromIndexExport,
  usageExportProcessing,
  cyfAdmin,
  setUsageBulkSelectionForEntity,
  submitBulkEditForEntity,
  toggleModal,
  auditReport,
  auditProcessing,
  showLoader,
  getUsageDataAuditReport,
  features,
}) => {
  const history = useHistory();

  // The reporting-year filter always mirrors the year the API actually returned. On landing the parent
  // page requests usage with no year, and the API responds with the most recent year that has data
  // (usageDataYear), so the table is filtered to that year automatically without any mismatch.
  const [reportingYear, setReportingYear] = useState(() => normalizeYear(usageDataYear));
  const [usageCategory, setUsageCategory] = useState(null);
  const [facility, setFacility] = useState(null);
  const [collapsedEntities, setCollapsedEntities] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Keep the filter value in sync with the year returned by the API.
  useEffect(() => {
    setReportingYear(normalizeYear(usageDataYear));
  }, [usageDataYear]);

  // Reset the other filters and selection when the organisation changes (data is refetched by the
  // parent page, which updates usageDataYear and therefore the reporting-year filter above).
  useEffect(() => {
    setUsageCategory(null);
    setFacility(null);
    setCollapsedEntities(new Set());
    setExpandedGroups(new Set());
    setSelectedRows(new Set());
  }, [currentOrganisation]);

  // Refetch the selected reporting year's totals from the server.
  const handleYearChange = (year) => {
    setReportingYear(normalizeYear(year));
    if (!currentOrganisation) return;
    getUsageTotals(year === 'all' ? 'all' : year);
  };

  // Filter grouped totals by entity/facility id (client-side), matching the previous behaviour.
  const filteredTotals = useMemo(() => {
    return (usageTotals || []).filter((total) => {
      if (usageCategory && String(total.entity) !== String(usageCategory)) return false;
      if (facility && String(total.facility) !== String(facility)) return false;
      return true;
    });
  }, [usageTotals, usageCategory, facility]);

  // Group totals by entity name and sort entities alphabetically, matching the previous behaviour.
  const groupedByEntity = useMemo(() => {
    const grouped = _.groupBy(filteredTotals, 'entityName');
    return Object.keys(grouped)
      .sort()
      .map((entityName) => {
        const totalsForEntity = grouped[entityName];
        const count = totalsForEntity.reduce((sum, item) => sum + (item.count || 0), 0);
        return {
          entityName,
          entityId: totalsForEntity[0]?.entity,
          count,
          totals: totalsForEntity,
        };
      });
  }, [filteredTotals]);

  const detailRowsByHash = useMemo(
    () => _.mapValues(_.groupBy(usageData || [], 'hash'), (rows) => _.sortBy(rows, ['dateStart', 'dateEnd'])),
    [usageData],
  );

  // Detail (individual usage) rows for a grouped total, matched by hash and sorted by date.
  const getDetailRows = (hash) => detailRowsByHash[hash] || [];

  // Usage records carry no quality field, so the tier is derived from the metric and how the
  // quantity was obtained. See getUsageQuality in utils/qualityHelpers.
  const metricsById = useMemo(() => _.keyBy(metrics || [], (metric) => String(metric.id)), [metrics]);

  const qualityByRowId = useMemo(
    () => new Map((usageData || []).map((row) => [row.id, getUsageQuality(row, { metricsById })])),
    [usageData, metricsById],
  );

  const qualityByHash = useMemo(
    () => _.mapValues(detailRowsByHash, (rows) => getGroupUsageQuality(rows, { metricsById })),
    [detailRowsByHash, metricsById],
  );

  // Reporting-year filter options built from org details, matching the previous "Data starts between" labels.
  const reportingYearOptions = useMemo(() => {
    if (!orgDetails?.usageYears?.minStartDate) return [{ value: 'all', label: 'All' }];

    const getStartDate = (endDate) => moment(endDate).add(-1, 'year').add(1, 'day').format('DD/MM/YYYY');

    let factorYear;
    if (orgDetails.reportingYearEndMonth > 6) {
      factorYear = new Date(orgDetails.usageYears.minStartDate).getFullYear();
    } else {
      factorYear = new Date(orgDetails.usageYears.minStartDate).getFullYear() - 1;
    }

    const reportingYearEndDates = [
      {
        endDate: new Date(factorYear + '-' + orgDetails.reportingYearEndMonth + '-' + orgDetails.reportingYearEndDay),
        value: factorYear,
      },
    ];

    while (
      orgDetails.reportingYearEndMonth == 12 && orgDetails.reportingYearEndDay == 31
        ? factorYear < new Date(orgDetails?.usageYears?.maxEndDate).getFullYear()
        : factorYear <= new Date(orgDetails?.usageYears?.maxEndDate).getFullYear()
    ) {
      factorYear++;
      const date = new Date(factorYear + '-' + orgDetails.reportingYearEndMonth + '-' + orgDetails.reportingYearEndDay);
      reportingYearEndDates.push({ endDate: date, value: factorYear });
    }

    const reportingDates = _.orderBy(
      _.uniqBy(reportingYearEndDates, 'endDate').map((item) => ({
        value: item.value,
        label: getStartDate(item.endDate) + ' - ' + moment(item.endDate).format('DD/MM/YYYY'),
      })),
      'value',
      'asc',
    );

    return [{ value: 'all', label: 'All' }, ...reportingDates];
  }, [
    orgDetails?.id,
    orgDetails?.usageYears?.minStartDate,
    orgDetails?.usageYears?.maxEndDate,
    orgDetails?.reportingYearEndMonth,
    orgDetails?.reportingYearEndDay,
  ]);

  const usageCategoryOptions = useMemo(
    () => _.uniqBy(usageTotals || [], 'entity').map((item) => ({ value: String(item.entity), label: item.entityName })),
    [usageTotals],
  );

  const facilityOptions = useMemo(
    () => (facilities || []).map((item) => ({ value: String(item.id), label: item.name })),
    [facilities],
  );

  // Selection helpers (admin bulk edit/delete).
  const selectedCount = selectedRows.size;
  const totalCount = usageData?.length || 0;

  const setSelectionForIds = (ids, shouldSelect) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const toggleEntitySelection = (entityId) => {
    const ids = (usageData || []).filter((d) => String(d.entity) === String(entityId)).map((d) => d.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedRows.has(id));
    setSelectionForIds(ids, !allSelected);
  };

  const toggleGroupSelection = (hash) => {
    const ids = getDetailRows(hash).map((d) => d.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedRows.has(id));
    setSelectionForIds(ids, !allSelected);
  };

  const toggleRowSelection = (id) => setSelectionForIds([id], !selectedRows.has(id));

  const isEntitySelected = (entityId) => {
    const ids = (usageData || []).filter((d) => String(d.entity) === String(entityId)).map((d) => d.id);
    return ids.length > 0 && ids.every((id) => selectedRows.has(id));
  };

  const isGroupSelected = (hash) => {
    const ids = getDetailRows(hash).map((d) => d.id);
    return ids.length > 0 && ids.every((id) => selectedRows.has(id));
  };

  const bulkEntityId = useMemo(() => {
    if (selectedRows.size === 0) return null;
    const selectedEntities = _.uniq(
      (usageData || []).filter((row) => selectedRows.has(row.id)).map((row) => row.entity),
    );
    if (selectedEntities.length !== 1) return null;
    return selectedEntities[0];
  }, [selectedRows, usageData]);

  const canBulkEdit = !!bulkEntityId;

  const handleBulkEdit = () => {
    if (!bulkEntityId || !cyfAdmin) return;
    setUsageBulkSelectionForEntity(bulkEntityId, Array.from(selectedRows));
    submitBulkEditForEntity(bulkEntityId);
    history.push(`/usage/${bulkEntityId}`);
  };

  const handleBulkDelete = () => {
    if (!bulkEntityId || !cyfAdmin) return;
    setUsageBulkSelectionForEntity(bulkEntityId, Array.from(selectedRows));
    submitBulkEditForEntity(bulkEntityId);
    toggleModal(true, 'usageData_entity-bulk_delete');
  };

  // Expansion helpers.
  const toggleEntity = (entityName) =>
    setCollapsedEntities((prev) => {
      const next = new Set(prev);
      next.has(entityName) ? next.delete(entityName) : next.add(entityName);
      return next;
    });

  const toggleGroup = (hash) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(hash) ? next.delete(hash) : next.add(hash);
      return next;
    });

  const handleExportAll = async () => {
    const filters = { exportClientUsage: true, organisation: currentOrganisation };
    if (usageCategory) filters.entity = usageCategory;
    if (facility) filters.facility = facility;
    await getItemsByTermsFromIndexExport('usageData', filters);
  };

  const handleEditRow = (row) => {
    getUsageOptions(row.entity).then(() => showUsageDataModal(true, row));
  };

  const handleCopyRow = (row) => {
    const copiedRow = { ...row, copy: true };
    delete copiedRow.id;
    getUsageOptions(row.entity).then(() => showUsageDataModal(true, copiedRow));
  };

  const handleDeleteRow = (row) => showUsageDataDeleteModal(true, row);

  const hasResults = groupedByEntity.length > 0;

  const renderUsageDetails = (row) => {
    if (!usageDetailLabels) return null;
    const details = [];
    Object.keys(usageDetailLabels).forEach((key) => {
      if (!row[key]) return;
      const type = types && _.find(types[key], { id: row[key] });
      const typeName = type && (type?.name || type?.priceType);
      if (typeName === undefined || type?.hideInReporting === true) return;
      details.push(
        <span key={key} className='block text-xs text-muted-foreground'>
          {`${usageDetailLabels[key]}: ${typeName}`}
        </span>,
      );
    });
    return details.length ? <div className='mt-1'>{details}</div> : null;
  };

  const renderQuality = (quality) => {
    if (!quality) return null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex'>
            <QualityTierBadge level={quality.level} size='sm' showIcon={false} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{quality.reason}</TooltipContent>
      </Tooltip>
    );
  };

  const renderDetailUsage = (row) => {
    if (row?.fx) {
      const inputCurrency = currencies?.find((c) => c.id === row?.fx?.fxInputCurrency);
      const outputCurrency = currencies?.find((c) => c.id === row?.fx?.fxOutputCurrency);
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='inline-flex items-center gap-1'>
              {row.rawUsage !== undefined && parseFloat(row.rawUsage)?.toFixed(2)} {inputCurrency?.symbol} Spend
              <Info size={14} strokeWidth={1.5} className='text-primary' />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className='text-center'>
              {inputCurrency?.symbol}
              {parseFloat(row?.rawUsage)?.toFixed(2)} {inputCurrency?.isoCode}
              &nbsp;is converted to&nbsp;
              {outputCurrency?.symbol}
              {parseFloat(row?.usage)?.toFixed(2)} {outputCurrency?.isoCode}
              <br />
              using FX Rate: {parseFloat(row?.fx?.fxRate)?.toFixed(4)}
            </p>
          </TooltipContent>
        </Tooltip>
      );
    }
    return (
      <>
        {row.usage !== undefined && parseFloat(row?.usage)?.toFixed(2)} {row?.metricName}
      </>
    );
  };

  return (
    <TooltipProvider>
      <Card className='w-full'>
        <CardHeader className='pb-4'>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='text-xl font-semibold'>Usage Summary</CardTitle>
              {cyfAdmin && (
                <p className='text-sm font-semibold text-muted-foreground'>
                  {selectedCount} of {totalCount} usage(s) selected
                </p>
              )}
            </div>
            <div className='flex items-center space-x-2'>
              {canBulkEdit && cyfAdmin && (
                <Button type='button' variant='outline' size='sm' onClick={handleBulkEdit}>
                  <Pencil size={16} strokeWidth={1.5} className='mr-2' />
                  Bulk Edit
                </Button>
              )}
              {canBulkEdit && cyfAdmin && (
                <Button type='button' variant='outline' size='sm' onClick={handleBulkDelete}>
                  <Trash2 size={16} strokeWidth={1.5} className='mr-2' />
                  Bulk Delete
                </Button>
              )}
              {auditReport && (
                <Button
                  onClick={getUsageDataAuditReport}
                  disabled={showLoader || auditProcessing}>
                  {auditProcessing ? (
                    <Loader2 size={16} strokeWidth={1.5} className='animate-spin mr-2' />
                  ) : (
                    <FileText size={16} strokeWidth={1.5} className='mr-2' />
                  )}
                  Audit Report
                  {features?.find((feature) => feature.featureKey === 'AUDIT_REPORT')?.isBeta && (
                    <BetaTag style='ml-2' />
                  )}
                </Button>
              )}
              <Button onClick={handleExportAll} disabled={usageExportProcessing}>
                {usageExportProcessing && <Loader2 size={16} strokeWidth={1.5} className='animate-spin mr-2' />}
                {!usageExportProcessing && <Download size={16} strokeWidth={1.5} className='mr-2' />}
                Export All
              </Button>
            </div>
          </div>

          <div className='py-4'>
            <Separator />
          </div>

          {/* Filters */}
          <div className='grid grid-cols-3 gap-4 mt-4 w-full'>
            <div className='flex flex-col space-y-1.5'>
              <Label>Data Starts Between</Label>
              <Select
                options={reportingYearOptions}
                value={reportingYear}
                onChange={handleYearChange}
                placeholder='Select year...'
                className='w-full'
              />
            </div>

            <div className='flex flex-col space-y-1.5'>
              <Label>Usage Category</Label>
              <Select
                options={usageCategoryOptions}
                value={usageCategory}
                onChange={setUsageCategory}
                placeholder='Select category...'
                className='w-full'
                searchable
                clearable
              />
            </div>

            <div className='flex flex-col space-y-1.5'>
              <Label>Facility</Label>
              <Select
                options={facilityOptions}
                value={facility}
                onChange={setFacility}
                placeholder='Select facility...'
                className='w-full'
                searchable
                clearable
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {processing ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-muted-foreground'>Loading usage data...</div>
            </div>
          ) : !hasResults ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-muted-foreground'>
                {usageCategory || facility
                  ? `There are no search results for ${
                      entities?.find((e) => String(e.id) === String(usageCategory))?.name || 'your filters'
                    }!`
                  : 'No Usage Data Found'}
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {cyfAdmin && <TableHead className='w-10' />}
                  <TableHead>Type</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead>Data starts between</TableHead>
                  <TableHead>Quality</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedByEntity.map((entity) => {
                  const entityExpanded = !collapsedEntities.has(entity.entityName);
                  return (
                    <React.Fragment key={entity.entityName}>
                      {/* Entity header row */}
                      <TableRow className='bg-background/60'>
                        {cyfAdmin && (
                          <TableCell className='whitespace-nowrap'>
                            <Checkbox
                              checked={isEntitySelected(entity.entityId)}
                              onCheckedChange={() => toggleEntitySelection(entity.entityId)}
                            />
                          </TableCell>
                        )}
                        <TableCell className='whitespace-nowrap font-semibold' colSpan={6}>
                          <div className='flex items-center gap-2'>
                            <Button
                              variant='ghost'
                              size='sm'
                              className='p-0 h-auto'
                              onClick={() => toggleEntity(entity.entityName)}>
                              {entityExpanded ? (
                                <ChevronDown size={16} strokeWidth={1.5} />
                              ) : (
                                <ChevronRight size={16} strokeWidth={1.5} />
                              )}
                            </Button>
                            <span>{entity.entityName}</span>
                            <span className='text-muted-foreground'>({entity.count})</span>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Grouped total rows */}
                      {entityExpanded &&
                        entity.totals.map((total) => {
                          const groupExpanded = expandedGroups.has(total.hash);
                          const detailRows = groupExpanded ? getDetailRows(total.hash) : [];
                          const groupFacility =
                            total.facilityName || facilities?.find((f) => String(f.id) === String(total.facility))?.name;
                          return (
                            <React.Fragment key={total.hash}>
                              <TableRow>
                                {cyfAdmin && (
                                  <TableCell className='whitespace-nowrap'>
                                    <Checkbox
                                      checked={isGroupSelected(total.hash)}
                                      onCheckedChange={() => toggleGroupSelection(total.hash)}
                                    />
                                  </TableCell>
                                )}
                                <TableCell className='whitespace-nowrap'>
                                  <div className='flex items-center gap-2 pl-4'>
                                    <Button
                                      variant='ghost'
                                      size='sm'
                                      className='p-0 h-auto'
                                      onClick={() => toggleGroup(total.hash)}>
                                      {groupExpanded ? (
                                        <ChevronDown size={16} strokeWidth={1.5} />
                                      ) : (
                                        <ChevronRight size={16} strokeWidth={1.5} />
                                      )}
                                    </Button>
                                    <span>{total.subentityName}</span>
                                  </div>
                                </TableCell>
                                <TableCell className='whitespace-nowrap'>
                                  {total.hasDifferentCurrencies ? (
                                    'Spend'
                                  ) : (
                                    <>
                                      {parseFloat(total.usageValue).toFixed(2)} {total.metricName}
                                    </>
                                  )}
                                </TableCell>
                                <TableCell className='whitespace-nowrap'>{groupFacility}</TableCell>
                                <TableCell className='whitespace-nowrap'>{getReportingWindow(total.endDate)}</TableCell>
                                <TableCell className='whitespace-nowrap'>{renderQuality(qualityByHash[total.hash])}</TableCell>
                                <TableCell />
                              </TableRow>

                              {/* Detail (individual usage) rows */}
                              {detailRows.map((row) => {
                                const quality = qualityByRowId.get(row.id);
                                return (
                                  <TableRow key={row.id}>
                                    {cyfAdmin && (
                                      <TableCell className='whitespace-nowrap'>
                                        <Checkbox
                                          checked={selectedRows.has(row.id)}
                                          onCheckedChange={() => toggleRowSelection(row.id)}
                                        />
                                      </TableCell>
                                    )}
                                    <TableCell className='align-top'>
                                      <div className='pl-10'>
                                        <span>{row.subentityName || row.entityName}</span>
                                        {renderUsageDetails(row)}
                                      </div>
                                    </TableCell>
                                    <TableCell className='whitespace-nowrap'>{renderDetailUsage(row)}</TableCell>
                                    <TableCell className='whitespace-nowrap'>{row.facilityName || groupFacility}</TableCell>
                                    <TableCell className='whitespace-nowrap'>
                                      {getDetailDateRange(row.dateStart, row.dateEnd)}
                                    </TableCell>
                                    <TableCell className='whitespace-nowrap'>{renderQuality(quality)}</TableCell>
                                    <TableCell className='whitespace-nowrap'>
                                      <div className='flex items-center space-x-2'>
                                        {canEdit && (
                                          <Button
                                            variant='ghost'
                                            size='lg'
                                            className='h-8 w-8 p-0 hover:text-secondary transition-colors'
                                            onClick={() => handleEditRow(row)}
                                            title='Edit'>
                                            <Pencil size={16} strokeWidth={1.5} />
                                          </Button>
                                        )}
                                        {canCopy && (
                                          <Button
                                            variant='ghost'
                                            size='lg'
                                            className='h-8 w-8 p-0 hover:text-secondary transition-colors'
                                            onClick={() => handleCopyRow(row)}
                                            title='Copy'>
                                            <Copy size={16} strokeWidth={1.5} />
                                          </Button>
                                        )}
                                        {canDelete && (
                                          <Button
                                            variant='ghost'
                                            size='lg'
                                            className='h-8 w-8 p-0 hover:text-destructive transition-colors'
                                            onClick={() => handleDeleteRow(row)}
                                            title='Delete'>
                                            <Trash2 size={16} strokeWidth={1.5} />
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

const mapStateToProps = (state) => {
  const usageTypeDetails = state?.admin?.adminPortal?.usageTypeDetails || [];
  const usageDetailLabels = usageTypeDetails.reduce((acc, curr) => {
    acc[curr.type] = curr.displayName;
    return acc;
  }, {});

  const features = state?.features;
  const availableFeatures = state.organisation?.details?.features;
  const auditReportFeature = features?.find((feature) => feature.featureKey === 'AUDIT_REPORT');
  const hasAuditReportAccess = availableFeatures?.includes('AUDIT_REPORT') || auditReportFeature?.enabledByDefault || false;

  return {
    usageTotals: state?.usageTotals || [],
    usageData: state?.usageData || [],
    facilities: state?.facility?.list || [],
    entities: state?.entities || [],
    currencies: state?.admin?.adminPortal?.currencies,
    metrics: state?.metric?.list || [],
    types: state?.metric?.types,
    usageDetailLabels,
    currentOrganisation: state.currentOrganisation,
    orgDetails: state?.organisation?.details,
    usageDataYear: state.usageDataYear,
    processing: state?.usageProcessing || false,
    canEdit: !!state?.organisation?.permissions?.usageData?.edit,
    canDelete: !!state?.organisation?.permissions?.usageData?.delete,
    canCopy: !!state?.organisation?.permissions?.usageData?.create,
    usageExportProcessing: state?.report?.download?.exportUsageData || false,
    cyfAdmin: state?.profile?.details?.cyfAdmin,
    auditReport: hasAuditReportAccess,
    auditProcessing: state?.report?.download?.auditReport || false,
    showLoader: state?.admin?.showLoader,
    features,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getUsageTotals: (year) => dispatch(USAGE_ACTIONS.getUsageTotals(year)),
  showUsageDataModal: (status, item) => dispatch(USAGE_ACTIONS.showUsageDataModal(status, item)),
  showUsageDataDeleteModal: (status, item) => dispatch(USAGE_ACTIONS.showUsageDataDeleteModal(status, item)),
  getUsageOptions: (entity) => dispatch(USAGE_ACTIONS.getUsageOptions(entity)),
  getItemsByTermsFromIndexExport: (index, filters, page, size) =>
    dispatch(ADMIN_ACTIONS.getItemsByTermsFromIndexExport(index, filters, page, size)),
  setUsageBulkSelectionForEntity: (entity, selectedIds) =>
    dispatch(ADMIN_ACTIONS.setUsageBulkSelectionForEntity(entity, selectedIds)),
  submitBulkEditForEntity: (entity) => dispatch(ADMIN_ACTIONS.submitBulkEditForEntity(entity)),
  toggleModal: (status, action, item, modalProps) => dispatch(ADMIN_ACTIONS.toggleModal(status, action, item, modalProps)),
  getUsageDataAuditReport: () => dispatch(USAGE_ACTIONS.getUsageDataAuditReport()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UsageSummaryTable);
